import { CometChatCalls } from '@cometchat/calls-sdk-javascript';
import { CometChatUIKit } from '@cometchat/chat-uikit-react';
import React, { useState } from 'react';
import { COMETCHAT_CONSTANTS } from './AppConstants';
import cometChatLogoDark from './assets/cometchat_logo_dark.svg';
import googleLogo from './assets/google.svg';
import './CometChatLoginWithGoogle.css';
import { signInAnonymouslyFirebase, signInWithGoogle } from './firebase';
import { initCallsSDK } from './utils/init-calls-sdk';

interface LoginWithFirebaseProps {
  onLoginSuccess: () => void;
}

const LoginWithFirebase: React.FC<LoginWithFirebaseProps> = ({
  onLoginSuccess,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [username, setUsername] = useState('');

  const createCometChatUser = async (
    uid: string,
    name: string,
    avatar?: string,
  ): Promise<void> => {
    const url = `https://${COMETCHAT_CONSTANTS.APP_ID}.api-${COMETCHAT_CONSTANTS.REGION.toLowerCase()}.${COMETCHAT_CONSTANTS.IS_STAGING ? 'cometchat-staging.com' : 'cometchat.io'}/v3/users`;

    const payload: any = {
      uid,
      name,
    };

    if (avatar) {
      payload.avatar = avatar;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
        appId: COMETCHAT_CONSTANTS.APP_ID,
        apiKey: COMETCHAT_CONSTANTS.AUTH_KEY,
      },
      body: JSON.stringify(payload),
    });

    console.log('User creation response status:', response.status);
    console.log(
      'User creation response headers:',
      Object.fromEntries(response.headers.entries()),
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error('User creation failed:', errorText);

      // Check if user already exists
      if (response.status === 409) {
        console.log('User already exists, continuing...');
        return; // User already exists, that's fine
      }

      throw new Error(
        `User creation failed: ${response.statusText} - ${errorText}`,
      );
    }

    const responseData = await response.json();
    console.log('User creation response:', responseData);
  };

  const loginToCometChat = async (
    uid: string,
    name: string,
    avatar?: string,
  ): Promise<void> => {
    try {
      await CometChatUIKit.login(uid);
      // UIKit login re-inits CometChatCalls without our hosts; restore them
      await initCallsSDK();
      await CometChatCalls.login(uid, COMETCHAT_CONSTANTS.AUTH_KEY);
      onLoginSuccess();
    } catch (error: any) {
      console.log('Initial login failed, error details:', {
        error,
        errorCode: error?.errorCode,
        errorDescription: error?.errorDescription,
        message: error?.message,
        code: error?.code,
      });

      // If user doesn't exist, create the user first
      if (
        error &&
        (error.code === 'ERR_UID_NOT_FOUND' ||
          error.errorCode === 'ERR_UID_NOT_FOUND' ||
          error.message?.includes('UID_NOT_FOUND') ||
          (error.errorCode === 'ERROR_UNKNOWN' &&
            error.errorDescription?.includes('Login failed'))) // Handle unknown errors specifically for login failure
      ) {
        try {
          await createCometChatUser(uid, name, avatar);
          console.log('CometChat user created successfully, retrying login...');

          // Add a small delay to ensure user is properly created
          await new Promise(resolve => setTimeout(resolve, 1000));

          // Retry login after user creation
          await CometChatUIKit.login(uid);

          // UIKit login re-inits CometChatCalls without our hosts; restore them
          await initCallsSDK();
          await CometChatCalls.login(uid, COMETCHAT_CONSTANTS.AUTH_KEY);
          onLoginSuccess();
        } catch (creationError) {
          console.error('Error creating CometChat user:', creationError);
          throw new Error('Failed to create user account. Please try again.');
        }
      } else {
        console.error('Error during CometChat login:', error);
        throw new Error(
          `Login failed: ${error?.errorDescription || error?.message || 'Unknown error'}`,
        );
      }
    }
  };

  const handleGoogleLogin = async () => {
    setIsLoading(true);
    try {
      console.log('Starting Google login...');
      const result = await signInWithGoogle();
      const user = result.user;

      if (user) {
        const uid = user.uid;
        const name = user.displayName || user.email || 'Google User';
        const avatar = user.photoURL || undefined;

        console.log('Google login successful:', {
          uid,
          name,
          email: user.email,
          avatar,
        });

        await loginToCometChat(uid, name, avatar);
      } else {
        throw new Error('Failed to get user from Google login');
      }
    } catch (error) {
      console.error('Google login failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAnonymousLogin = async () => {
    if (!username.trim()) {
      return;
    }

    setIsLoading(true);
    try {
      console.log('Starting anonymous login with username:', username.trim());

      // First, sign in anonymously with Firebase
      const result = await signInAnonymouslyFirebase();
      console.log('Firebase anonymous login result:', result);

      const user = result.user;

      if (user) {
        const uid = user.uid;
        const name = username.trim();

        console.log('Firebase anonymous login successful:', {
          uid,
          name,
          isAnonymous: user.isAnonymous,
          email: user.email,
        });

        // Now login to CometChat with the Firebase UID
        await loginToCometChat(uid, name);
      } else {
        throw new Error(
          'Failed to create anonymous user - no user returned from Firebase',
        );
      }
    } catch (error: any) {
      console.error('Anonymous login failed:', error);

      // Provide more specific error messages
      if (error.code === 'auth/operation-not-allowed') {
        console.log(
          'Anonymous authentication is not enabled. Please contact support.',
        );
      } else if (error.code === 'auth/too-many-requests') {
        console.log('Too many requests. Please try again later.');
      } else {
        console.log(
          `Anonymous login failed: ${error.message || 'Please try again.'}`,
        );
      }
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <div className="cometchat-login-with-google__wrapper">
      <div>
        <img src={cometChatLogoDark} alt="CometChat Logo" />
      </div>

      <div className="cometchat-login-with-google">
        <div className="cometchat-login-with-google__input-wrapper">
          <input
            type="text"
            placeholder="Enter your name"
            value={username}
            onChange={e => setUsername(e.target.value)}
            className="cometchat-login-with-google__input"
            onKeyDown={e => {
              if (e.key === 'Enter' && !isLoading && username.trim()) {
                handleAnonymousLogin();
              }
            }}
          />
          <div
            onClick={handleAnonymousLogin}
            style={{
              cursor: isLoading || !username.trim() ? 'not-allowed' : 'pointer',
            }}
            className={`cometchat-login-with-google__click cometchat-login-with-google__click-continue ${isLoading || !username.trim() ? 'disabled' : ''}`}>
            <button
              className="cometchat-login__text"
              disabled={isLoading || !username.trim()}>
              Continue
            </button>
          </div>
        </div>

        <div className="cometchat-login-with-google__separator-wrapper">
          <span className="cometchat-login-with-google__separator">Or</span>
        </div>

        <div
          className="cometchat-login-with-google__click"
          onClick={handleGoogleLogin}
          style={{
            pointerEvents: isLoading ? 'none' : 'auto',
            opacity: isLoading ? 0.6 : 1,
          }}>
          <img src={googleLogo} alt="Google" />
          <div className="cometchat-login-with-google__text">Google</div>
        </div>
      </div>
    </div>
  );
};

export default LoginWithFirebase;
